import { useContext, useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Button,
  Text,
  Flex,
  useToast,
  Divider,
  Checkbox,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
  Input,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { useBilling } from '../../../store/client/BillingContext';
import { HttpContext } from '../../../context/HttpContext';
import CustomToast from '../../../common/CustomToast';

const MIN_OTHER_LENGTH = 6;

const REASON_FORM_DEFAULTS = {
  reason: '',
  reason_other: '',
  price_range: '',
  alternative_tool: '',
  missing_feature: '',
};

const CANCELLATION_REASONS = [
  { value: 'features_needed', label: "I didn't find the features I needed" },
  {
    value: 'too_expensive',
    label: 'PassEntry is too expensive for my use case',
  },
  { value: 'not_ready_yet', label: "I'm not ready to implement this yet" },
  {
    value: 'setup_too_complex',
    label: 'The setup or integration was too complex',
  },
  { value: 'found_another', label: 'I found another solution' },
  { value: 'just_exploring', label: 'I was just exploring' },
  { value: 'other', label: 'Other' },
];

const FOLLOW_UP_CONFIG = {
  too_expensive: {
    key: 'price_range',
    label: 'What price range would work better for you?',
    placeholder: 'e.g. £X–Y per month',
  },
  found_another: {
    key: 'alternative_tool',
    label: 'Which tool did you choose?',
    placeholder: '',
  },
  features_needed: {
    key: 'missing_feature',
    label: 'Which feature was missing?',
    placeholder: '',
  },
};

const CancelTrialModal = ({ isOpen, onClose, onSuccess }) => {
  const { authAxios } = useContext(HttpContext);
  const { refresh } = useBilling();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { subscription } = useBilling();
  const [isChecked, setIsChecked] = useState(false);
  const [proceedWithCancel, setProceedWithCancel] = useState(false);

  const {
    control,
    watch,
    reset,
    getValues,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: REASON_FORM_DEFAULTS,
    mode: 'onChange',
  });

  const reason = watch('reason');
  const reasonOther = watch('reason_other');

  useEffect(() => {
    if (isOpen) {
      setIsChecked(false);
      setProceedWithCancel(false);
      reset(REASON_FORM_DEFAULTS);
    }
  }, [isOpen, reset]);

  const followUp = reason ? FOLLOW_UP_CONFIG[reason] : null;

  const trimmedOther = (reasonOther || '').trim();
  const canSubmitReasonStep =
    reason && (reason !== 'other' || trimmedOther.length >= MIN_OTHER_LENGTH);

  const buildPayload = () => {
    const values = getValues();
    const payload = { cancellation_reason: values.reason };

    const meta = {};
    if (values.reason === 'other' && (values.reason_other || '').trim()) {
      meta.reason_other = values.reason_other.trim();
    }
    if (
      values.reason === 'too_expensive' &&
      (values.price_range || '').trim()
    ) {
      meta.price_range = values.price_range.trim();
    }
    if (
      values.reason === 'found_another' &&
      (values.alternative_tool || '').trim()
    ) {
      meta.alternative_tool = values.alternative_tool.trim();
    }
    if (
      values.reason === 'features_needed' &&
      (values.missing_feature || '').trim()
    ) {
      meta.missing_feature = values.missing_feature.trim();
    }
    if (Object.keys(meta).length > 0) {
      payload.cancellation_reason_meta = meta;
    }

    return payload;
  };

  const handleCancelTrial = async () => {
    if (!canSubmitReasonStep) return;
    setLoading(true);

    try {
      const apiUrl = `/internal/v1/billing/subscriptions/cancel_trial`;
      const payload = buildPayload();

      await authAxios.post(apiUrl, payload);
      await refresh();

      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Trial cancelled successfully"
            description=""
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage =
        err.response?.data?.errors?.[0] || 'Something went wrong';
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Error"
            description={errorMessage}
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent py={6} px={8}>
        <ModalCloseButton />
        <Text textStyle="heading4" fontSize="24px" mt="10px" textAlign="center">
          Cancel your trial?
        </Text>
        <ModalBody my={3}>
          <Divider border="0.5px solid lightGrey" mb={4} />
          {!proceedWithCancel ? (
            <Flex direction="column" w="100%">
              <Text textStyle="bodyFamilyBold" fontSize="16px">
                You're about to cancel your trial.
              </Text>
              <Text textStyle="bodyFamilyRegular" fontSize="16px" my="10px">
                Cancelling will <b>immediately end your trial</b>, expire all
                trial passes and remove access to trial features.
              </Text>
              <Text textStyle="bodyFamilyRegular" fontSize="16px">
                If you'd like to keep exploring PassEntry, you still have{' '}
                <b>{subscription?.daysRemainingInTrial} days remaining</b> on
                your trial.
              </Text>
              <Flex
                w="100%"
                direction="column"
                justifyContent="center"
                alignItems="center"
                gap="25px"
              >
                <Checkbox
                  mt="25px"
                  colorScheme="orange"
                  size="md"
                  isChecked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                  alignSelf="center"
                  lineHeight="1.2"
                  data-testid="cancel-trial-checkbox"
                >
                  <Text textStyle="bodyFamilyRegular" fontSize="14px">
                    I understand that my trial will end immediately and all
                    trial passes will expire.
                  </Text>
                </Checkbox>
                <Button variant="gradient" w="100%" onClick={onClose}>
                  Keep trial
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  data-testid="confirm-cancel-trial-button"
                  onClick={() => setProceedWithCancel(true)}
                  isDisabled={!isChecked}
                >
                  <Text>Cancel trial</Text>
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Flex direction="column" w="100%" gap={4}>
              <FormControl isRequired>
                <FormLabel variant="templateBuilderLabel" mb={3}>
                  Before you go, let us know what didn’t work for you.
                </FormLabel>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <RadioGroup value={value} onChange={onChange} name="reason">
                      <Flex direction="column" gap={2}>
                        {CANCELLATION_REASONS.map(
                          ({ value: optionValue, label }) => (
                            <Radio
                              key={optionValue}
                              value={optionValue}
                              colorScheme="orange"
                              size="md"
                            >
                              <Text
                                textStyle="bodyFamilyRegular"
                                fontSize="15px"
                              >
                                {label}
                              </Text>
                            </Radio>
                          )
                        )}
                      </Flex>
                    </RadioGroup>
                  )}
                />
              </FormControl>

              {reason === 'other' && (
                <FormControl isRequired isInvalid={!!errors.reason_other}>
                  <FormLabel variant="templateBuilderLabel">
                    Please specify (required)
                  </FormLabel>
                  <Textarea
                    {...register('reason_other', {
                      validate: (value, formValues) => {
                        if (formValues.reason !== 'other') return true;
                        const t = (value || '').trim();
                        if (t.length === 0) {
                          return 'Please specify your reason.';
                        }
                        if (t.length < MIN_OTHER_LENGTH) {
                          return `Please provide a bit more detail.`;
                        }
                        return true;
                      },
                    })}
                    placeholder="Tell us more..."
                    rows={3}
                    bg="softWhite"
                    resize="none"
                  />
                  <FormErrorMessage>
                    {errors.reason_other?.message}
                  </FormErrorMessage>
                </FormControl>
              )}

              {followUp && followUp.key === 'price_range' && (
                <FormControl>
                  <FormLabel variant="templateBuilderLabel">
                    {followUp.label}
                  </FormLabel>
                  <Input
                    {...register('price_range')}
                    placeholder={followUp.placeholder}
                    bg="softWhite"
                  />
                </FormControl>
              )}

              {followUp && followUp.key === 'alternative_tool' && (
                <FormControl>
                  <FormLabel variant="templateBuilderLabel">
                    {followUp.label}
                  </FormLabel>
                  <Input
                    {...register('alternative_tool')}
                    placeholder={followUp.placeholder}
                    bg="softWhite"
                  />
                </FormControl>
              )}

              {followUp && followUp.key === 'missing_feature' && (
                <FormControl>
                  <FormLabel variant="templateBuilderLabel">
                    {followUp.label}
                  </FormLabel>
                  <Input
                    {...register('missing_feature')}
                    placeholder={followUp.placeholder}
                    bg="softWhite"
                  />
                </FormControl>
              )}

              <Flex gap={3} mt={2}>
                <Button
                  variant="secondary"
                  onClick={() => setProceedWithCancel(false)}
                  isDisabled={loading}
                >
                  <Text>Back</Text>
                </Button>
                <Button
                  flex={1}
                  variant="gradient"
                  onClick={handleCancelTrial}
                  isLoading={loading}
                  loadingText="Cancelling..."
                  isDisabled={!canSubmitReasonStep}
                  data-testid="submit-cancel-trial-button"
                >
                  Cancel trial
                </Button>
              </Flex>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CancelTrialModal;
